import { Link } from 'react-router-dom';
import { Shield, Scale, Bot, Landmark, Check } from 'lucide-react';

const PILLARS = [
  {
    icon: Shield,
    title: 'Banking-grade security',
    items: [
      'Phishing-resistant sign-in with zero-downtime credential rotation',
      'Secure-by-default session handling — no token leakage to client storage',
      'Rate limiting on every sensitive endpoint',
      'Brute-force lockout with cooldown',
      'Multi-factor authentication available for all accounts',
      'Industry-standard password hashing with enforced strength',
    ],
  },
  {
    icon: Scale,
    title: 'GDPR & data rights',
    items: [
      'Article 7 — explicit, versioned consent capture at registration',
      'Article 15 — one-click data export (full JSON of your vault)',
      'Article 17 — right to erasure with 30-day grace + audit trail',
      'Article 30 — record-of-processing with retention horizons enforced',
      'PII redacted from production logs',
      'Automatic retention sweep purges expired tokens & shares hourly',
    ],
  },
  {
    icon: Bot,
    title: 'AI governance',
    items: [
      'Every AI inference logged in an audit register (6-year horizon)',
      'AI-generated content clearly disclosed in the UI',
      'AI features can be disabled per tenant, per workflow, per data subject — honoured at the request boundary',
      'No customer data used for model training — ever',
      'Human-in-the-loop required for any hiring decision',
      'EU AI Act Article 22 opt-out is the architecture, not a setting',
    ],
  },
  {
    icon: Landmark,
    title: 'Banking compliance',
    items: [
      '6-year retention on audit logs & completed references (POCA/FCA aligned)',
      'Companies House verification on all employer accounts',
      'OpenSanctions screening hooks for KYC workflows',
      'Tamper-evident audit log across every regulated artefact',
      'Cryptographic vault with time-limited employer access',
      'Anonymity-by-default for references; reveal only on candidate consent',
    ],
  },
];

const FAQ = [
  {
    q: 'Where is my data stored?',
    a: 'Customer data sits in encrypted clusters hosted in EU regions, with field-level encryption on personal data and rotation-friendly keys. Full sub-processor list and region map available under NDA.',
  },
  {
    q: 'How do I export or delete my data?',
    a: 'Any signed-in user can hit Settings → Data & Privacy → Export (GDPR Art. 15) or Request erasure (Art. 17). Erasure runs after a 30-day cool-off so accidental deletions can be reversed.',
  },
  {
    q: 'Do you use my data to train AI models?',
    a: 'No. We use enterprise LLM providers via API only, with explicit no-training contractual terms. Your data is never sent to model providers for training purposes.',
  },
  {
    q: 'Is Vault Hire SOC 2 / ISO 27001 certified?',
    a: 'We are pre-pilot. SOC 2 Type I readiness assessment is on the FY26 roadmap. The controls listed above are already operational and independently audit-ready.',
  },
];

function PublicTopBar() {
  return (
    <header className="border-b border-[var(--vh-border)] bg-[var(--vh-bg-base)]/85 backdrop-blur sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)]" data-testid="trust-brand">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--vh-text-secondary)]">
          <Link to="/jobs" className="hover:text-[var(--vh-brand-primary)]">Jobs board</Link>
          <Link to="/companies" className="hover:text-[var(--vh-brand-primary)]">Companies</Link>
          <Link to="/trust" className="text-[var(--vh-brand-primary)] font-semibold">Trust</Link>
          <Link to="/pricing" className="hover:text-[var(--vh-brand-primary)]">Pricing</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 rounded-full text-sm text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)]">Sign in</Link>
          <Link to="/register" className="px-4 py-2 rounded-full text-sm bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]">Get started</Link>
        </div>
      </div>
    </header>
  );
}

export default function TrustPage() {
  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)]" data-testid="trust-page">
      <PublicTopBar />

      <section className="max-w-6xl mx-auto px-6 pt-20 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-4">Trust &amp; Compliance Centre</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.04] mb-5" data-testid="trust-title">
          Built for regulated hiring.
        </h1>
        <p className="text-lg text-[var(--vh-text-secondary)] max-w-3xl mx-auto leading-relaxed">
          Vault Hire is engineered to satisfy the controls expected of banking, fintech and regulated-industry recruitment. Below is what we have implemented today — verifiable, not aspirational.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="rounded-3xl p-7 md:p-9 bg-[var(--vh-brand-primary)] text-white flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="md:max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-white/70 mb-2">For banking buyers · NDA-gated</p>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">The case against legacy ATS architecture</h3>
            <p className="text-sm text-white/80 leading-relaxed">
              A board-ready briefing for the Senior Manager who will personally sign off on this system under SMCR. Why retention is now the liability, what we hold vs. what a legacy ATS holds, and the line that closes the deal. NDA + owner approval required.
            </p>
          </div>
          <a
            href="/walkthrough/"
            data-testid="trust-nda-briefing-link"
            className="shrink-0 inline-flex items-center justify-center px-6 py-3 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] text-sm font-semibold"
          >
            Request the briefing →
          </a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-2 gap-6">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl p-7 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]" data-testid={`pillar-${p.title.toLowerCase().replace(/\W+/g, '-')}`}>
                <div className="w-10 h-10 rounded-xl bg-[var(--vh-bg-muted)] text-[var(--vh-brand-primary)] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-[var(--vh-text-primary)] mb-3">{p.title}</h3>
                <ul className="text-sm text-[var(--vh-text-secondary)] space-y-2">
                  {p.items.map((i) => (
                    <li key={`${p.title}-${i}`} className="flex gap-2">
                      <Check className="w-4 h-4 text-[var(--vh-accent)] shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-3 text-center">FAQ</p>
        <h2 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-8 text-center">Common questions.</h2>
        <div className="space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl p-5 group" data-testid={`faq-${f.q.slice(0, 20).toLowerCase().replace(/\W+/g, '-')}`}>
              <summary className="cursor-pointer font-display text-lg text-[var(--vh-text-primary)] flex items-center justify-between gap-3">
                <span>{f.q}</span>
                <span className="text-[var(--vh-accent)] text-xl leading-none group-open:rotate-45 transition" aria-hidden="true">+</span>
              </summary>
              <p className="mt-3 text-sm text-[var(--vh-text-secondary)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-24 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-3">Procurement &amp; due-diligence</p>
        <h2 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-3">Everything your procurement team asks for, in one place.</h2>
        <p className="text-[var(--vh-text-secondary)] mb-7 max-w-2xl mx-auto">
          Our Legal &amp; Compliance Library hosts the four artefacts banking procurement and DPO teams ask for in week one: Instructions for Use (EU AI Act Art. 13), Data Processing Agreement (UK GDPR Art. 28), sub-processor list, and a fillable DPIA template.
        </p>
        <a
          href="/legal/"
          data-testid="trust-legal-library-link"
          className="inline-block px-6 py-3 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-semibold"
        >
          Open the Legal &amp; Compliance Library →
        </a>
        <p className="text-xs text-[var(--vh-text-muted)] mt-6">
          Need a SOC 2 readiness statement, region map, or pen-test summary? Email <a href="mailto:trust@vault-hire.com" className="underline hover:text-[var(--vh-brand-primary)]">trust@vault-hire.com</a> — reply within 1 working day.
        </p>
      </section>

      <footer className="border-t border-[var(--vh-border)] bg-[var(--vh-bg-muted)] py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-[var(--vh-text-muted)]">
          © {new Date().getFullYear()} Vault Hire · <Link to="/" className="underline hover:text-[var(--vh-brand-primary)]">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
